/**
 * RepoPickerModal - Modal for selecting a repository from an installation.
 */

import { useState, useEffect, useMemo, type ReactNode } from "react";
import { useGitStore } from "@/stores/gitStore";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Loader2, Search, Lock, Globe, FolderGit2 } from "lucide-react";

interface RepoPickerModalProps {
  workspaceId: number;
  installationId: number;
  onSelect: (repo: string, installationId: number) => void;
  trigger?: ReactNode;
}

export function RepoPickerModal({
  workspaceId,
  installationId,
  onSelect,
  trigger,
}: RepoPickerModalProps) {
  const { repos, fetchRepos, isLoading } = useGitStore();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (open) {
      fetchRepos(workspaceId, installationId);
    }
  }, [open, workspaceId, installationId, fetchRepos]);

  const filteredRepos = useMemo(() => {
    if (!search.trim()) return repos;
    const query = search.toLowerCase();
    return repos.filter(
      (repo) =>
        repo.fullName.toLowerCase().includes(query) ||
        repo.description?.toLowerCase().includes(query)
    );
  }, [repos, search]);

  const handleSelect = (repo: string) => {
    onSelect(repo, installationId);
    setOpen(false);
    setSearch("");
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button>
            <FolderGit2 className="w-4 h-4 mr-2" />
            Select Repository
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-2xl w-[90vw] sm:w-full overflow-hidden">
        <DialogHeader>
          <DialogTitle>Select Repository</DialogTitle>
          <DialogDescription>
            Choose a repository to link to this project
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search repositories..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>

          <ScrollArea className="h-[300px] border rounded-md">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : filteredRepos.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                {repos.length === 0
                  ? "No repositories available"
                  : "No repositories match your search"}
              </div>
            ) : (
              <div className="p-2 space-y-1">
                {filteredRepos.map((repo) => (
                  <button
                    key={repo.id}
                    onClick={() => handleSelect(repo.fullName)}
                    className="w-full text-left p-3 rounded-md hover:bg-muted transition-colors"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          {repo.private ? (
                            <Lock className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                          ) : (
                            <Globe className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                          )}
                          <span className="font-medium truncate">{repo.fullName}</span>
                        </div>
                        {repo.description && (
                          <p className="text-sm text-muted-foreground truncate mt-1 ml-6">
                            {repo.description}
                          </p>
                        )}
                      </div>
                      <Badge variant="outline" className="text-xs flex-shrink-0">
                        {repo.defaultBranch}
                      </Badge>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </ScrollArea>
        </div>
      </DialogContent>
    </Dialog>
  );
}
